/**
 * Minimal glTF 2.0 binary (.glb) reader for base character models (Meshy exports): triangle primitives with
 * POSITION / NORMAL / TEXCOORD_0 / JOINTS_0 / WEIGHTS_0, the base-colour texture, the node tree and one skin.
 * Everything is flattened to one indexed mesh in model space (node transforms applied for static meshes).
 */

export interface GltfSkin {
  /** Joint node indices, in skin order. */
  joints: number[];
  /** Column-major 4x4 inverse bind matrices, one per joint. */
  inverseBind: Float32Array;
}

export interface GltfNode {
  name: string;
  parent: number;
  children: number[];
  translation: [number, number, number];
  rotation: [number, number, number, number];
  scale: [number, number, number];
}

export interface GltfModel {
  positions: Float32Array;
  normals: Float32Array;
  uvs: Float32Array;
  indices: Uint32Array;
  /** 4 joint indices per vertex (skin order), present when the mesh is skinned. */
  joints: Uint16Array | null;
  weights: Float32Array | null;
  baseColor: ImageBitmap | null;
  baseColorFactor: [number, number, number, number];
  nodes: GltfNode[];
  skin: GltfSkin | null;
  animations: string[];
}

interface Accessor { bufferView?: number; byteOffset?: number; componentType: number; count: number; type: string; normalized?: boolean }
interface BufferView { buffer: number; byteOffset?: number; byteLength: number; byteStride?: number }

const COMPONENTS: Record<string, number> = { SCALAR: 1, VEC2: 2, VEC3: 3, VEC4: 4, MAT4: 16 };

function readAccessor(json: any, bin: DataView, index: number): Float32Array | Uint32Array | Uint16Array {
  const acc: Accessor = json.accessors[index];
  const view: BufferView = json.bufferViews[acc.bufferView ?? 0];
  const n = COMPONENTS[acc.type];
  const base = (view.byteOffset ?? 0) + (acc.byteOffset ?? 0);
  const size = { 5120: 1, 5121: 1, 5122: 2, 5123: 2, 5125: 4, 5126: 4 }[acc.componentType] ?? 4;
  const stride = view.byteStride ?? size * n;
  const count = acc.count * n;
  const out = acc.componentType === 5126 || acc.normalized ? new Float32Array(count)
    : acc.componentType === 5125 ? new Uint32Array(count) : new Uint16Array(count);
  for (let i = 0; i < acc.count; i++) {
    for (let c = 0; c < n; c++) {
      const o = base + i * stride + c * size;
      let v: number;
      switch (acc.componentType) {
        case 5126: v = bin.getFloat32(o, true); break;
        case 5125: v = bin.getUint32(o, true); break;
        case 5123: v = bin.getUint16(o, true); if (acc.normalized) v /= 65535; break;
        case 5122: v = bin.getInt16(o, true); if (acc.normalized) v = Math.max(v / 32767, -1); break;
        case 5121: v = bin.getUint8(o); if (acc.normalized) v /= 255; break;
        default: v = bin.getInt8(o); if (acc.normalized) v = Math.max(v / 127, -1); break;
      }
      out[i * n + c] = v;
    }
  }
  return out;
}

/** Column-major 4x4 multiply: out = a * b. */
export function mat4Mul(a: Float32Array | number[], b: Float32Array | number[], out = new Float32Array(16)): Float32Array {
  for (let c = 0; c < 4; c++) for (let r = 0; r < 4; r++) {
    let s = 0;
    for (let k = 0; k < 4; k++) s += a[k * 4 + r] * b[c * 4 + k];
    out[c * 4 + r] = s;
  }
  return out;
}

export function trsMatrix(t: number[], q: number[], s: number[], out = new Float32Array(16)): Float32Array {
  const [x, y, z, w] = q;
  const xx = x * x, yy = y * y, zz = z * z, xy = x * y, xz = x * z, yz = y * z, wx = w * x, wy = w * y, wz = w * z;
  out[0] = (1 - 2 * (yy + zz)) * s[0]; out[1] = 2 * (xy + wz) * s[0]; out[2] = 2 * (xz - wy) * s[0]; out[3] = 0;
  out[4] = 2 * (xy - wz) * s[1]; out[5] = (1 - 2 * (xx + zz)) * s[1]; out[6] = 2 * (yz + wx) * s[1]; out[7] = 0;
  out[8] = 2 * (xz + wy) * s[2]; out[9] = 2 * (yz - wx) * s[2]; out[10] = (1 - 2 * (xx + yy)) * s[2]; out[11] = 0;
  out[12] = t[0]; out[13] = t[1]; out[14] = t[2]; out[15] = 1;
  return out;
}

export async function loadGlb(url: string): Promise<GltfModel> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`${url}: HTTP ${response.status}`);
  const buf = await response.arrayBuffer();
  if (buf.byteLength < 20) throw new Error(`${url}: incomplete GLB`);
  const dv = new DataView(buf);
  if (dv.getUint32(0, true) !== 0x46546c67) throw new Error(`${url}: not a GLB`);
  if (dv.getUint32(4,true) !== 2 || dv.getUint32(8,true) !== buf.byteLength) throw new Error(`${url}: invalid GLB header`);
  let off = 12;
  let json: any = null;
  let bin: DataView | null = null;
  while (off < buf.byteLength) {
    if (off + 8 > buf.byteLength) throw new Error(`${url}: truncated chunk`);
    const len = dv.getUint32(off, true), type = dv.getUint32(off + 4, true);
    if (off + 8 + len > buf.byteLength) throw new Error(`${url}: truncated chunk data`);
    if (type === 0x4e4f534a) json = JSON.parse(new TextDecoder().decode(new Uint8Array(buf, off + 8, len)));
    else if (type === 0x004e4942) bin = new DataView(buf, off + 8, len);
    off += 8 + len;
  }
  if (!json || !bin) throw new Error(`${url}: missing JSON or BIN chunk`);
  if ((json.extensionsRequired ?? []).length) console.warn('[gltf] required extensions not supported:', json.extensionsRequired);

  // node tree + world matrices (rest)
  const nodes: GltfNode[] = (json.nodes ?? []).map((n: any) => ({
    name: n.name ?? '', parent: -1, children: n.children ?? [],
    translation: n.translation ?? [0, 0, 0], rotation: n.rotation ?? [0, 0, 0, 1], scale: n.scale ?? [1, 1, 1],
    matrix: n.matrix,
  }));
  nodes.forEach((n, i) => n.children.forEach((c) => { nodes[c].parent = i; }));
  const world: Float32Array[] = [];
  const worldOf = (i: number): Float32Array => {
    if (world[i]) return world[i];
    const n = nodes[i] as GltfNode & { matrix?: number[] };
    const local = n.matrix ? new Float32Array(n.matrix) : trsMatrix(n.translation, n.rotation, n.scale);
    world[i] = n.parent >= 0 ? mat4Mul(worldOf(n.parent), local) : local;
    return world[i];
  };

  const pos: number[] = [], nrm: number[] = [], uv: number[] = [], idx: number[] = [], jnt: number[] = [], wgt: number[] = [];
  let skinned = false;
  let materialIndex = -1;
  const meshNodes = nodes.map((n, i) => ({ i, mesh: (json.nodes[i] as any).mesh, skin: (json.nodes[i] as any).skin })).filter((x) => x.mesh !== undefined);
  for (const mn of meshNodes) {
    const m = worldOf(mn.i);
    const isSkinned = mn.skin !== undefined;
    for (const prim of json.meshes[mn.mesh].primitives) {
      if ((prim.mode ?? 4) !== 4) continue;
      const a = prim.attributes;
      const P = readAccessor(json, bin, a.POSITION) as Float32Array;
      const N = a.NORMAL !== undefined ? readAccessor(json, bin, a.NORMAL) as Float32Array : null;
      const T = a.TEXCOORD_0 !== undefined ? readAccessor(json, bin, a.TEXCOORD_0) as Float32Array : null;
      const J = a.JOINTS_0 !== undefined ? readAccessor(json, bin, a.JOINTS_0) : null;
      const W = a.WEIGHTS_0 !== undefined ? readAccessor(json, bin, a.WEIGHTS_0) as Float32Array : null;
      const base = pos.length / 3;
      const vc = P.length / 3;
      for (let v = 0; v < vc; v++) {
        let x = P[v * 3], y = P[v * 3 + 1], z = P[v * 3 + 2];
        let nx = N ? N[v * 3] : 0, ny = N ? N[v * 3 + 1] : 1, nz = N ? N[v * 3 + 2] : 0;
        if (!isSkinned) {
          const tx = m[0] * x + m[4] * y + m[8] * z + m[12], ty = m[1] * x + m[5] * y + m[9] * z + m[13], tz = m[2] * x + m[6] * y + m[10] * z + m[14];
          const qx = m[0] * nx + m[4] * ny + m[8] * nz, qy = m[1] * nx + m[5] * ny + m[9] * nz, qz = m[2] * nx + m[6] * ny + m[10] * nz;
          x = tx; y = ty; z = tz; const l = Math.hypot(qx, qy, qz) || 1; nx = qx / l; ny = qy / l; nz = qz / l;
        }
        pos.push(x, y, z); nrm.push(nx, ny, nz);
        uv.push(T ? T[v * 2] : 0, T ? T[v * 2 + 1] : 0);
        if (J && W) { skinned = true; for (let k = 0; k < 4; k++) { jnt.push(J[v * 4 + k]); wgt.push(W[v * 4 + k]); } }
        else { jnt.push(0, 0, 0, 0); wgt.push(1, 0, 0, 0); }
      }
      if (prim.indices !== undefined) { const I = readAccessor(json, bin, prim.indices); for (let k = 0; k < I.length; k++) idx.push(I[k] + base); }
      else for (let k = 0; k < vc; k++) idx.push(base + k);
      if (materialIndex < 0 && prim.material !== undefined) materialIndex = prim.material;
    }
  }

  // base colour texture
  let baseColor: ImageBitmap | null = null;
  let baseColorFactor: [number, number, number, number] = [1, 1, 1, 1];
  const mat = materialIndex >= 0 ? json.materials[materialIndex] : null;
  const pbr = mat?.pbrMetallicRoughness;
  if (pbr?.baseColorFactor) baseColorFactor = pbr.baseColorFactor;
  const texIndex = pbr?.baseColorTexture?.index;
  if (texIndex !== undefined) {
    const img = json.images[json.textures[texIndex].source];
    let blob: Blob;
    if (img.bufferView !== undefined) {
      const v: BufferView = json.bufferViews[img.bufferView];
      const start = bin.byteOffset + (v.byteOffset ?? 0);
      blob = new Blob([(bin.buffer as ArrayBuffer).slice(start, start + v.byteLength)], { type: img.mimeType ?? 'image/png' });
    } else blob = await (await fetch(new URL(img.uri, new URL(url, location.href)))).blob();
    baseColor = await createImageBitmap(blob, { imageOrientation: 'none', colorSpaceConversion: 'none' });
  }

  let skin: GltfSkin | null = null;
  if (skinned && json.skins?.length) {
    const s = json.skins[0];
    const ib = s.inverseBindMatrices !== undefined ? readAccessor(json, bin, s.inverseBindMatrices) as Float32Array : null;
    skin = { joints: s.joints, inverseBind: ib ?? new Float32Array(s.joints.flatMap(() => [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1])) };
  }

  return {
    positions: new Float32Array(pos), normals: new Float32Array(nrm), uvs: new Float32Array(uv), indices: new Uint32Array(idx),
    joints: skinned ? new Uint16Array(jnt) : null, weights: skinned ? new Float32Array(wgt) : null,
    baseColor, baseColorFactor, nodes, skin, animations: (json.animations ?? []).map((a: any, i: number) => a.name ?? `anim${i}`),
  };
}
